import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import apiClient from "./utils/apiClient";

// Generate a 64-char hex secret using window.crypto when available so the
// caller never has to invent one. Falls back to Math.random for safety on
// older browsers — those use cases are not security-critical because the
// user can always paste their own value over the top.
function generateSecret(byteLen = 32) {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const bytes = window.crypto.getRandomValues(new Uint8Array(byteLen));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return Array.from({ length: byteLen * 2 }, () =>
    Math.floor(Math.random() * 16).toString(16),
  ).join("");
}

const fmtTs = (epoch) => {
  if (!epoch) return "—";
  const n = Number(epoch);
  if (!Number.isFinite(n)) return String(epoch);
  const ms = n < 1e12 ? n * 1000 : n;
  return new Date(ms).toLocaleString();
};

const ROLE_OPTIONS = ["Admin"];

export default function ApiClients() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionResult, setActionResult] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [form, setForm] = useState({ name: "", secret: "", role: "Admin" });
  const [addSecretVisible, setAddSecretVisible] = useState(true);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Per-row secret visibility — by default each row's secret is masked, the
  // eye-button toggles it. Tracking by id keeps each row independent.
  const [visibleSecretIds, setVisibleSecretIds] = useState(() => new Set());
  const toggleSecretVisible = (id) => {
    setVisibleSecretIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get("/api-client-list");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch api clients:", err);
      setError("Failed to load API clients. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const openAdd = () => {
    setForm({ name: "", secret: generateSecret(), role: "Admin" });
    setAddSecretVisible(true);
    setAddOpen(true);
  };

  const closeAdd = () => {
    if (addBusy) return;
    setAddOpen(false);
  };

  const doCreate = async () => {
    if (!form.name.trim() || !form.secret.trim()) {
      setActionResult({ severity: "error", text: "Name and secret are required." });
      return;
    }
    setAddBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        secret: form.secret.trim(),
        roles: form.role ? [form.role] : [],
        created: Date.now(),
      };
      await apiClient.post("/api-client", payload);
      setActionResult({
        severity: "success",
        text: `API client "${payload.name}" created. Copy the secret now — once you close this you won't see it again unless you reveal it from the row.`,
      });
      setAddOpen(false);
      await fetchClients();
    } catch (err) {
      console.error("create failed", err);
      setActionResult({ severity: "error", text: `Create failed: ${err.message || err}` });
    } finally {
      setAddBusy(false);
    }
  };

  const doDelete = async () => {
    if (!pendingDelete?.id) return;
    setDeleteBusy(true);
    try {
      await apiClient.delete(`/api-client/${encodeURIComponent(pendingDelete.id)}`);
      setActionResult({ severity: "success", text: `Deleted API client "${pendingDelete.name || pendingDelete.id}".` });
      setPendingDelete(null);
      await fetchClients();
    } catch (err) {
      console.error("delete failed", err);
      setActionResult({ severity: "error", text: `Delete failed: ${err.message || err}` });
    } finally {
      setDeleteBusy(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setActionResult({ severity: "success", text: `${label} copied to clipboard.` });
    } catch (err) {
      setActionResult({ severity: "error", text: `Copy failed: ${err.message || err}` });
    }
  };

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (Number(b.created) || 0) - (Number(a.created) || 0)),
    [rows],
  );

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<VpnKeyIcon />}
          title="API Clients"
          subtitle={`${rows.length} client${rows.length === 1 ? "" : "s"}`}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={openAdd}
                startIcon={<AddIcon />}
                sx={{ backgroundColor: "#fff", color: "#1a2b44", "&:hover": { backgroundColor: "#e6e6e6" } }}
              >
                Add Client
              </Button>
              <Button
                variant="outlined"
                onClick={fetchClients}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.4)",
                  "&:hover": {
                    borderColor: "#fff",
                    backgroundColor: "rgba(255,255,255,0.06)",
                  },
                }}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </Stack>
          }
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {actionResult && (
          <Alert
            severity={actionResult.severity}
            onClose={() => setActionResult(null)}
            sx={{ mb: 2 }}
          >
            {actionResult.text}
          </Alert>
        )}

        <DataCard noPadding>
          {loading && rows.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderCellSx}>Name</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Roles</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Secret</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Created</TableCell>
                    <TableCell sx={tableHeaderCellSx} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No API clients yet. Add one to give an external integration access via the <code>X-API-Key</code> header.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sorted.map((c) => {
                      const visible = visibleSecretIds.has(c.id);
                      const secretDisplay = visible ? c.secret : "•".repeat(Math.min(24, (c.secret || "").length || 16));
                      return (
                        <TableRow key={c.id} hover>
                          <TableCell>
                            <Box>{c.name || "—"}</Box>
                            <Box sx={{ fontFamily: "monospace", fontSize: 11, color: "text.secondary" }}>{c.id}</Box>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {(c.roles || []).map((r) => (
                                <Chip key={r} label={r} size="small" />
                              ))}
                              {(c.roles || []).length === 0 && <Box sx={{ color: "text.secondary" }}>—</Box>}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Box sx={{ fontFamily: "monospace", fontSize: 12 }}>{secretDisplay}</Box>
                              <Tooltip title={visible ? "Hide" : "Show"}>
                                <IconButton size="small" onClick={() => toggleSecretVisible(c.id)}>
                                  {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              {visible && c.secret && (
                                <Tooltip title="Copy">
                                  <IconButton size="small" onClick={() => copyToClipboard(c.secret, "Secret")}>
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtTs(c.created)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Delete">
                              <span>
                                <IconButton size="small" color="error" onClick={() => setPendingDelete(c)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataCard>
      </PageContainer>

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Add API Client</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            External integrations authenticate by sending the secret in the{" "}
            <code>X-API-Key</code> header. The role(s) you pick here drive the same
            authorization gates as a logged-in user with those roles.
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              placeholder="e.g. Reporting Pipeline"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              autoFocus
            />
            <Box>
              <Box sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>Role</Box>
              <Select
                size="small"
                fullWidth
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              >
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </Box>
            <TextField
              label="Secret"
              type={addSecretVisible ? "text" : "password"}
              value={form.secret}
              onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
              fullWidth
              InputProps={{
                sx: { fontFamily: "monospace", fontSize: 13 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={addSecretVisible ? "Hide" : "Show"}>
                      <IconButton size="small" onClick={() => setAddSecretVisible((v) => !v)}>
                        {addSecretVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Regenerate">
                      <IconButton size="small" onClick={() => setForm((f) => ({ ...f, secret: generateSecret() }))}>
                        <AutorenewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyToClipboard(form.secret, "Secret")}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Alert severity="info">
              Copy the secret now — it's stored as-is in the database and shown in
              the table, but you should treat it like a password. The integration
              owner won't be able to retrieve it from anywhere else.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd} disabled={addBusy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={doCreate}
            disabled={addBusy || !form.name.trim() || !form.secret.trim()}
          >
            {addBusy ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(pendingDelete)} onClose={() => !deleteBusy && setPendingDelete(null)}>
        <DialogTitle>Delete API client?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete API client <code>{pendingDelete?.name || pendingDelete?.id}</code>.
            Any external integration using its secret will start getting <code>401</code>
            responses within ~10 seconds (the auth cache miss-TTL).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleteBusy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doDelete} disabled={deleteBusy}>
            {deleteBusy ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
